/**
 * Orquestração do lifecycle da fila (rehydrate, flush periódico, unload).
 */

import { sendEventBeaconOnly } from '../../utils/sendEvent';
import { initNetworkDetection, onNetworkChange } from '../network';
import { initTabCoordinator } from '../tabCoordinator';
import {
  loadQueueFromStorage,
  migrateQueueStorageV1ToV2,
} from '../storage/queueStore';
import {
  persistPendingUnloadItems,
  loadPendingUnloadItems,
  clearPendingUnloadItems,
} from '../pendingUnload';
import { recoverStuckProcessingItems } from '../recovery/processingRecovery';
import { getAliveTabIds } from '../coordination/tabRegistry';
import { getTabId } from '../tabCoordinator';
import { recordQueueSize, recordEventPhase } from '../telemetry';
import { runRecoveryPlan } from '../recovery/recoveryPlanner';
import {
  FLUSH_INTERVAL_MS,
  type QueueItem,
  type QueueStats,
  type QueueStatus,
} from './types';
import {
  hydrateAllCheckpointsFromIdb,
  pruneStaleReplayCheckpoints,
} from '../replay/persistentReplay';
import {
  clearQueue,
  getQueueLength,
  getQueueMutable,
  getQueueSnapshot,
  persistQueue,
  setMaxQueueItemsLimit,
  setQueue,
} from './queueState';
import {
  enqueueEvent as enqueueEventInternal,
  sendPageViewImmediately,
  sendEventImmediately,
} from './enqueueEngine';
import { runFlushCycle } from './flushEngine';
import { orderItemsForBeaconUnload } from './replayEngine';
import { scheduleCoalescedFlush, clearPendingFlushTimer } from './flushBudget';

let flushIntervalId: ReturnType<typeof setInterval> | null = null;
let flushMutex: Promise<void> = Promise.resolve();

function scheduleFlushAfterEnqueue(item: QueueItem): void {
  const urgent = item.priority === 'high';
  scheduleCoalescedFlush(() => void flushQueue(), urgent);
}

export async function rehydrate(): Promise<void> {
  if (typeof window === 'undefined') return;
  migrateQueueStorageV1ToV2();
  await hydrateAllCheckpointsFromIdb();
  await pruneStaleReplayCheckpoints();
  const now = Date.now();
  try {
    const loaded = await loadQueueFromStorage();
    const { items } = runRecoveryPlan({
      localQueue: getQueueMutable(),
      loaded,
      now,
      tabId: getTabId(),
      aliveTabIds: getAliveTabIds(),
      maxQueueItems: 100,
    });
    setQueue(items);
    recoverStuckProcessingItems(getQueueMutable(), now);
    const pending = loadPendingUnloadItems();
    if (pending.length > 0) {
      getQueueMutable().push(
        ...pending.map(p => ({
          ...p,
          status: 'queued' as QueueStatus,
          sessionSequence: p.sessionSequence ?? 0,
        }))
      );
      clearPendingUnloadItems();
    }
    recordQueueSize(getQueueLength());
  } catch {
    clearQueue();
  }
}

export function flushQueue(): Promise<void> {
  flushMutex = flushMutex.then(() => runFlushCycle()).catch(() => {});
  return flushMutex;
}

export function flushQueueWithBeacon(): void {
  if (getQueueLength() === 0) return;
  setUnloadingFlag(true);
  const ordered = orderItemsForBeaconUnload([...getQueueSnapshot()]);
  persistPendingUnloadItems(ordered);
  clearQueue();
  persistQueue();
  for (const item of ordered) {
    recordEventPhase(item.id, 'sent');
    sendEventBeaconOnly(
      item.params,
      item.id,
      item.occurredAt ?? item.createdAt
    );
  }
  setUnloadingFlag(false);
}

export function startPeriodicFlush(
  intervalMs: number = FLUSH_INTERVAL_MS
): void {
  if (typeof window === 'undefined') return;
  initNetworkDetection();
  initTabCoordinator();
  void rehydrate();
  stopPeriodicFlush();
  onNetworkChange(online => {
    if (online) void flushQueue();
  });
  flushIntervalId = setInterval(() => {
    void flushQueue();
  }, intervalMs);
}

export function stopPeriodicFlush(): void {
  if (flushIntervalId) {
    clearInterval(flushIntervalId);
    flushIntervalId = null;
  }
}

export function setUnloadingFlag(value: boolean): void {
  if (typeof window !== 'undefined') {
    (
      window as Window & { __gomarkePixelUnloading?: boolean }
    ).__gomarkePixelUnloading = value;
  }
}

export function setupUnloadFlush(): void {
  // mantido para compat; tracker usa unload.ts
}

export function getQueueStats(): QueueStats {
  const queue = getQueueSnapshot();
  if (queue.length === 0) {
    return { size: 0, oldestCreatedAt: null, processing: 0, failed: 0 };
  }
  return {
    size: queue.length,
    oldestCreatedAt: Math.min(...queue.map(i => i.createdAt)),
    processing: queue.filter(i => i.status === 'processing').length,
    failed: queue.filter(i => i.status === 'failed').length,
  };
}

export function _testSetMaxQueueItems(limit: number): void {
  setMaxQueueItemsLimit(limit);
}

export function _testClearQueue(): void {
  clearPendingFlushTimer();
  clearQueue();
  persistQueue();
}

export function _testSetQueue(items: QueueItem[]): void {
  setQueue(items);
}

export function enqueueEvent(
  params: Parameters<typeof enqueueEventInternal>[0],
  priority: Parameters<typeof enqueueEventInternal>[1] = 'low'
): string {
  return enqueueEventInternal(params, priority, scheduleFlushAfterEnqueue);
}

export { sendPageViewImmediately, sendEventImmediately, getQueueLength };
